import React, { useEffect, useState } from "react";
import { db, auth } from "../firebaseConfig";
import {
  collection, onSnapshot, doc, updateDoc, addDoc,
  query, orderBy, Timestamp,
} from "firebase/firestore";
import styled, { keyframes, css, createGlobalStyle } from "styled-components";
import { enqueueSnackbar } from "notistack";

// ─── Constants ────────────────────────────────────────────────────────────────

const CURRENCIES = {
  GBP: { symbol: "£", color: "#2563eb", bg: "#eff6ff", flag: "🇬🇧" },
  PKR: { symbol: "₨", color: "#059669", bg: "#ecfdf5", flag: "🇵🇰" },
  USD: { symbol: "$",  color: "#7c3aed", bg: "#f5f3ff", flag: "🇺🇸" },
};

const CURRENCY_OPTIONS = ["GBP", "PKR", "USD"];

const getDisplayName = (email) => {
  if (!email) return "User";
  return email.split("@")[0].replace(/\./g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
};

// ─── Animations ───────────────────────────────────────────────────────────────

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(14px); }
  to   { opacity: 1; transform: translateY(0); }
`;
const fadeIn = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;
const slideRight = keyframes`
  from { opacity: 0; transform: translateX(28px); }
  to   { opacity: 1; transform: translateX(0); }
`;
const pulse = keyframes`
  0%, 100% { opacity: 1; transform: scale(1); }
  50%       { opacity: 0.55; transform: scale(0.82); }
`;

// ─── Global ───────────────────────────────────────────────────────────────────

const GlobalStyle = createGlobalStyle`
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600&family=DM+Mono:wght@400;500&display=swap');
  *, *::before, *::after { box-sizing: border-box; }
`;

// ─── Layout ───────────────────────────────────────────────────────────────────

const Page = styled.div`
  min-height: 100vh;
  background: #f1f5f9;
  font-family: 'DM Sans', sans-serif;
  padding: 36px 24px 80px;
`;
const Inner = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

// ─── Header ───────────────────────────────────────────────────────────────────

const Header = styled.div`
  margin-bottom: 28px;
  animation: ${fadeUp} 0.4s ease both;
`;
const Badge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  color: #64748b;
  margin-bottom: 6px;

  &::before {
    content: '';
    width: 6px; height: 6px;
    border-radius: 50%;
    background: #22c55e;
    animation: ${pulse} 2s ease infinite;
  }
`;
const PageTitle = styled.h1`
  font-size: 26px;
  font-weight: 600;
  color: #0f172a;
  margin: 0 0 4px;
  letter-spacing: -0.4px;
`;
const PageSub = styled.p`
  font-size: 13px;
  color: #64748b;
  margin: 0;
`;

// ─── Stats ────────────────────────────────────────────────────────────────────

const StatsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  margin-bottom: 22px;
  animation: ${fadeUp} 0.4s ease 0.05s both;
  @media (min-width: 640px) { grid-template-columns: repeat(4, 1fr); }
`;
const StatCard = styled.div`
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 16px;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0;
    height: 3px;
    background: ${({ $color }) => $color};
    border-radius: 12px 12px 0 0;
  }

  .stat-label  { font-size: 11px; font-weight: 600; letter-spacing: 0.6px; text-transform: uppercase; color: #94a3b8; margin-bottom: 6px; }
  .stat-value  { font-size: 22px; font-weight: 600; color: #0f172a; letter-spacing: -0.5px; line-height: 1; font-family: 'DM Mono', monospace; }
  .stat-sub    { font-size: 11px; color: #94a3b8; margin-top: 4px; }
  .stat-icon   { position: absolute; right: 14px; top: 50%; transform: translateY(-50%); font-size: 22px; opacity: 0.15; }
`;

// ─── Filter Bar ───────────────────────────────────────────────────────────────

const FilterBar = styled.div`
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 14px 16px;
  margin-bottom: 20px;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
  animation: ${fadeUp} 0.4s ease 0.1s both;
`;
const FilterLabel = styled.span`
  font-size: 11px; font-weight: 600; letter-spacing: 0.6px;
  text-transform: uppercase; color: #94a3b8; white-space: nowrap;
`;
const FilterGroup = styled.div`display: flex; flex-wrap: wrap; gap: 6px; flex: 1;`;
const FilterChip = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 5px 12px;
  border-radius: 20px;
  font-size: 12px; font-weight: 500;
  font-family: 'DM Sans', sans-serif;
  cursor: pointer; transition: all 0.15s;
  border: 1.5px solid ${({ $active, $color }) => $active ? $color : "#e2e8f0"};
  background: ${({ $active, $bg }) => $active ? $bg : "transparent"};
  color: ${({ $active, $color }) => $active ? $color : "#64748b"};
  &:hover { border-color: ${({ $color }) => $color}; color: ${({ $color }) => $color}; }
`;
const SearchInput = styled.input`
  flex: 1; min-width: 180px;
  padding: 7px 12px; font-size: 13px;
  font-family: 'DM Sans', sans-serif;
  border: 1.5px solid #e2e8f0; border-radius: 8px;
  outline: none; background: #f8fafc; color: #0f172a; transition: all 0.15s;
  &:focus { border-color: #2563eb; background: white; box-shadow: 0 0 0 3px #2563eb18; }
  &::placeholder { color: #cbd5e1; }
`;

// ─── Cards Grid ───────────────────────────────────────────────────────────────

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(270px, 1fr));
  gap: 14px;
  animation: ${fadeUp} 0.4s ease 0.15s both;
`;
const PaymentCard = styled.div`
  background: white;
  border-radius: 14px;
  border: 1.5px solid #e2e8f0;
  padding: 18px;
  cursor: pointer;
  transition: all 0.18s ease;
  position: relative;
  overflow: hidden;
  animation: ${fadeUp} 0.35s ease both;
  animation-delay: ${({ $i }) => Math.min($i * 40, 300)}ms;

  &::before {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0;
    height: 3px;
    background: ${({ $color }) => $color};
  }
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 8px 24px ${({ $color }) => $color}22;
    border-color: ${({ $color }) => $color}66;
  }
`;
const CardTop = styled.div`
  display: flex; align-items: center;
  justify-content: space-between; margin-bottom: 14px;
`;
const CurrencyTag = styled.div`
  display: inline-flex; align-items: center; gap: 6px;
  padding: 4px 10px; border-radius: 20px;
  font-size: 12px; font-weight: 700;
  background: ${({ $bg }) => $bg}; color: ${({ $color }) => $color};
  font-family: 'DM Mono', monospace;
`;
const CardField = styled.div`
  display: flex; align-items: baseline; gap: 6px;
  margin-bottom: 6px; font-size: 13px;

  .lbl  { font-size: 10px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px; color: #94a3b8; min-width: 58px; }
  .val  { color: #334155; font-weight: 500; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .mono { font-family: 'DM Mono', monospace; font-size: 12px; color: #475569; }
`;
const GBPBadge = styled.div`
  display: flex; align-items: center; justify-content: space-between;
  margin-top: 12px; padding: 8px 12px;
  border-radius: 9px; background: #f8fafc; border: 1px solid #e2e8f0;

  .label  { font-size: 10px; font-weight: 600; color: #94a3b8; text-transform: uppercase; letter-spacing: 0.5px; }
  .amount { font-size: 15px; font-weight: 700; color: #1d4ed8; font-family: 'DM Mono', monospace; }
`;
const EmptyState = styled.div`
  grid-column: 1 / -1; text-align: center; padding: 64px 24px; color: #94a3b8;
  .icon { font-size: 40px; margin-bottom: 12px; opacity: 0.4; }
  .msg  { font-size: 15px; font-weight: 500; color: #64748b; }
  .sub  { font-size: 13px; margin-top: 4px; }
`;
const StatusPill = styled.span`
  display: inline-flex; align-items: center; gap: 5px;
  padding: 3px 10px; border-radius: 20px;
  font-size: 11px; font-weight: 600;
  background: ${({ $s }) =>
    $s === "CONFIRMED" ? "#ecfdf5" : $s === "REJECTED" ? "#fef2f2" : "#fffbeb"};
  color: ${({ $s }) =>
    $s === "CONFIRMED" ? "#065f46" : $s === "REJECTED" ? "#991b1b" : "#92400e"};

  &::before {
    content: ''; width: 5px; height: 5px; border-radius: 50%;
    background: ${({ $s }) =>
      $s === "CONFIRMED" ? "#10b981" : $s === "REJECTED" ? "#ef4444" : "#f59e0b"};
    ${({ $s }) => $s === "PENDING" && css`animation: ${pulse} 1.4s ease infinite;`}
  }
`;

// ─── Modal ────────────────────────────────────────────────────────────────────

const Overlay = styled.div`
  position: fixed; inset: 0;
  background: rgba(15, 23, 42, 0.52);
  backdrop-filter: blur(4px);
  display: flex; align-items: center; justify-content: center;
  z-index: 200; padding: 20px;
  animation: ${fadeIn} 0.2s ease both;
`;
const Modal = styled.div`
  background: white; border-radius: 18px;
  width: min(540px, 100%); max-height: 88vh; overflow-y: auto;
  box-shadow: 0 24px 64px rgba(0,0,0,0.2);
  animation: ${slideRight} 0.25s ease both;
`;
const ModalHeader = styled.div`
  padding: 22px 24px 0;
  display: flex; align-items: center; justify-content: space-between;
`;
const ModalTitleWrap = styled.div`
  display: flex; align-items: center; gap: 12px;

  .icon-wrap {
    width: 42px; height: 42px; border-radius: 11px;
    background: ${({ $bg }) => $bg};
    display: flex; align-items: center; justify-content: center;
    font-size: 20px;
  }
  h3   { font-size: 16px; font-weight: 600; color: #0f172a; margin: 0; }
  span { font-size: 12px; color: #94a3b8; }
`;
const CloseBtn = styled.button`
  width: 32px; height: 32px; border-radius: 8px;
  border: 1px solid #e2e8f0; background: #f8fafc;
  color: #64748b; font-size: 16px;
  display: flex; align-items: center; justify-content: center;
  cursor: pointer; transition: all 0.12s;
  &:hover { background: #fee2e2; color: #ef4444; border-color: #fca5a5; }
`;
const ModalBody = styled.div`padding: 20px 24px;`;
const SectionDivider = styled.div`
  font-size: 10px; font-weight: 600; color: #94a3b8;
  text-transform: uppercase; letter-spacing: 1px;
  margin: 16px 0 10px;
  display: flex; align-items: center; gap: 8px;
  &::after { content: ''; flex: 1; height: 1px; background: #f1f5f9; }
`;
const InfoGrid = styled.div`display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin-bottom: 4px;`;

const InfoRow = styled.div`
  padding: 10px 13px; background: #f8fafc;
  border-radius: 9px; border: 1px solid #f1f5f9;
  grid-column: ${({ $span }) => $span ? "1 / -1" : "auto"};

  .info-label { font-size: 10px; font-weight: 600; color: #94a3b8; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 4px; }
`;

// ─── Editable Field Styles ────────────────────────────────────────────────────

const EditInput = styled.input`
  width: 100%; padding: 4px 0; font-size: 14px; font-weight: 500;
  font-family: ${({ $mono }) => $mono ? "'DM Mono', monospace" : "'DM Sans', sans-serif"};
  color: #0f172a; background: transparent;
  border: none; border-bottom: 1.5px solid #e2e8f0;
  outline: none; transition: border-color 0.15s;
  &:focus { border-bottom-color: #2563eb; }
`;

const EditSelect = styled.select`
  width: 100%; padding: 4px 0; font-size: 14px; font-weight: 500;
  font-family: 'DM Sans', sans-serif;
  color: #0f172a; background: transparent;
  border: none; border-bottom: 1.5px solid #e2e8f0;
  outline: none; cursor: pointer; transition: border-color 0.15s;
  appearance: none;
  &:focus { border-bottom-color: #2563eb; }
`;

const GBPHighlight = styled.div`
  display: flex; align-items: center; justify-content: space-between;
  padding: 16px 18px;
  background: linear-gradient(135deg, #eff6ff, #f0fdf4);
  border: 1px solid #bfdbfe; border-radius: 12px; margin-bottom: 18px;

  .hl-label  { font-size: 12px; font-weight: 600; color: #1e40af; text-transform: uppercase; letter-spacing: 0.5px; }
  .hl-amount { font-size: 24px; font-weight: 700; color: #1d4ed8; font-family: 'DM Mono', monospace; }
  .hl-rate   { font-size: 11px; color: #60a5fa; margin-top: 2px; }
`;
const AdminNotesArea = styled.textarea`
  width: 100%; padding: 10px 13px; font-size: 13px;
  font-family: 'DM Sans', sans-serif;
  border: 1.5px solid #e2e8f0; border-radius: 9px;
  outline: none; background: #f8fafc; color: #0f172a;
  transition: all 0.15s; resize: vertical; min-height: 80px; box-sizing: border-box;
  &:focus { border-color: #2563eb; background: white; box-shadow: 0 0 0 3px #2563eb15; }
  &::placeholder { color: #cbd5e1; }
`;
const ModalFooter = styled.div`
  padding: 14px 24px 22px; display: flex; gap: 10px;
  border-top: 1px solid #f1f5f9;
`;
const ConfirmBtn = styled.button`
  flex: 1; padding: 12px; font-size: 14px; font-weight: 600;
  font-family: 'DM Sans', sans-serif;
  color: white; background: #059669; border: none; border-radius: 10px;
  cursor: pointer; transition: all 0.15s;
  display: flex; align-items: center; justify-content: center; gap: 8px;
  &:hover:not(:disabled) { background: #047857; transform: translateY(-1px); box-shadow: 0 6px 20px #05996944; }
  &:disabled { opacity: 0.55; cursor: not-allowed; }
`;
const RejectBtn = styled.button`
  padding: 12px 18px; font-size: 14px; font-weight: 600;
  font-family: 'DM Sans', sans-serif;
  color: #dc2626; background: #fef2f2;
  border: 1.5px solid #fca5a5; border-radius: 10px;
  cursor: pointer; transition: all 0.15s;
  &:hover:not(:disabled) { background: #fee2e2; border-color: #ef4444; }
  &:disabled { opacity: 0.55; cursor: not-allowed; }
`;
const CancelBtn = styled.button`
  padding: 12px 16px; font-size: 14px; font-weight: 500;
  font-family: 'DM Sans', sans-serif;
  color: #64748b; background: #f1f5f9; border: none; border-radius: 10px;
  cursor: pointer; transition: all 0.15s;
  &:hover { background: #e2e8f0; color: #0f172a; }
`;

const EditHint = styled.div`
  display: flex; align-items: center; gap: 6px;
  font-size: 11px; color: #94a3b8; margin-bottom: 14px;
  padding: 7px 11px; background: #f8fafc; border-radius: 7px;
  border: 1px dashed #e2e8f0;

  &::before { content: '✏️'; font-size: 12px; }
`;

// ─── Component ────────────────────────────────────────────────────────────────

export default function PaymentConfirmationPage() {
  const [requests,   setRequests]   = useState([]);
  const [selected,   setSelected]   = useState(null);
  const [editData,   setEditData]   = useState(null);   // ← editable copy
  const [adminNotes, setAdminNotes] = useState("");
  const [saving,     setSaving]     = useState(false);
  const [filterCur,  setFilterCur]  = useState("All");
  const [search,     setSearch]     = useState("");

  useEffect(() => {
    const q = query(
      collection(db, "PaymentConfirmationRequests"),
      orderBy("timestamp", "desc")
    );
    return onSnapshot(q, (snap) => {
      const all = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setRequests(all.filter((r) => r.status === "PENDING"));
    });
  }, []);

  const openModal = (req) => {
    setSelected(req);
    // initialise editable copy from the request
    setEditData({
      folderNo:   req.folderNo   || "",
      agent:      req.agent      || "",
      bankName:   req.bankName   || "",
      date:       req.date       || "",
      currency:   req.currency   || "GBP",
      rate:       req.rate       || "",
      reference:  req.reference  || "",
      remarks:    req.remarks    || "",
      paymentGBP: req.paymentGBP || "",
      paymentPKR: req.paymentPKR || "",
      paymentUSD: req.paymentUSD || "",
    });
    setAdminNotes("");
  };

  const closeModal = () => { setSelected(null); setEditData(null); setAdminNotes(""); };

  // Helper: update a single field
  const setField = (key, val) => setEditData((prev) => ({ ...prev, [key]: val }));

  // Derive live GBP equivalent from editData
  const liveGBP = () => {
    if (!editData) return 0;
    if (editData.currency === "GBP") return parseFloat(editData.paymentGBP) || 0;
    if (editData.currency === "PKR") {
      const rate = parseFloat(editData.rate);
      const pkr  = parseFloat(editData.paymentPKR);
      return rate && pkr ? pkr / rate : parseFloat(editData.paymentGBP) || 0;
    }
    if (editData.currency === "USD") {
      const rate = parseFloat(editData.rate);
      const usd  = parseFloat(editData.paymentUSD);
      return rate && usd ? usd / rate : parseFloat(editData.paymentGBP) || 0;
    }
    return parseFloat(editData.paymentGBP) || 0;
  };

  const handleConfirm = async () => {
    if (!selected || !editData) return;
    setSaving(true);
    const user = auth.currentUser;

    // Recalculate paymentGBP from live calc so it stays accurate
    const computedGBP = liveGBP().toFixed(2);

    try {
      await addDoc(collection(db, "bankPayments"), {
        folderNo:    editData.folderNo,
        agent:       editData.agent,
        paymentGBP:  computedGBP,
        paymentPKR:  editData.paymentPKR,
        paymentUSD:  editData.paymentUSD,
        currency:    editData.currency,
        rate:        editData.rate,
        reference:   editData.reference,
        date:        editData.date,
        remarks:     editData.remarks,
        bankName:    editData.bankName,
        confirmedBy: getDisplayName(user?.email),
        adminNotes:  adminNotes || "-",
        timestamp:   Timestamp.now(),
      });

      await updateDoc(doc(db, "PaymentConfirmationRequests", selected.id), {
        ...editData,
        paymentGBP:  computedGBP,
        status:      "CONFIRMED",
        confirmedBy: getDisplayName(user?.email),
        confirmedAt: Timestamp.now(),
        adminNotes:  adminNotes || "-",
      });

      enqueueSnackbar("Payment confirmed & saved to ledger ✓", { variant: "success" });
      closeModal();
    } catch (err) {
      console.error(err);
      enqueueSnackbar("Error confirming payment", { variant: "error" });
    } finally {
      setSaving(false);
    }
  };

  const handleReject = async () => {
    if (!selected || !editData) return;
    setSaving(true);
    const user = auth.currentUser;
    try {
      await updateDoc(doc(db, "PaymentConfirmationRequests", selected.id), {
        ...editData,
        status:     "REJECTED",
        rejectedBy: getDisplayName(user?.email),
        rejectedAt: Timestamp.now(),
        adminNotes: adminNotes || "-",
      });
      enqueueSnackbar("Payment request rejected", { variant: "error" });
      closeModal();
    } catch (err) {
      console.error(err);
      enqueueSnackbar("Error rejecting payment", { variant: "error" });
    } finally {
      setSaving(false);
    }
  };

  // ── Filtering ──────────────────────────────────────────────────────────────

  const filtered = requests.filter((r) => {
    const matchCur = filterCur === "All" || r.currency === filterCur;
    const q = search.toLowerCase();
    const matchSearch = !q ||
      (r.folderNo  || "").toLowerCase().includes(q) ||
      (r.agent     || "").toLowerCase().includes(q) ||
      (r.bankName  || "").toLowerCase().includes(q) ||
      (r.reference || "").toLowerCase().includes(q);
    return matchCur && matchSearch;
  });

  // ── Stats ──────────────────────────────────────────────────────────────────

  const totalGBP = requests.reduce((s, r) => s + (parseFloat(r.paymentGBP) || 0), 0);
  const gbpCount = requests.filter((r) => r.currency === "GBP").length;
  const pkrCount = requests.filter((r) => r.currency === "PKR").length;
  const usdCount = requests.filter((r) => r.currency === "USD").length;

  // derive current currency config from editData (live, so currency changes update instantly)
  const selCur = editData ? (CURRENCIES[editData.currency] || CURRENCIES.GBP) : null;

  // native amount label in the highlight bar
  const nativeAmt = () => {
    if (!editData) return "";
    if (editData.currency === "PKR") return editData.paymentPKR;
    if (editData.currency === "USD") return editData.paymentUSD;
    return editData.paymentGBP;
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <>
      <GlobalStyle />
      <Page>
        <Inner>

          {/* ── Header ── */}
          <Header>
            <Badge>Live Queue</Badge>
            <PageTitle>Payment Confirmations</PageTitle>
            <PageSub>Review pending payment requests — confirm to write into the bank ledger</PageSub>
          </Header>

          {/* ── Stats ── */}
          <StatsRow>
            <StatCard $color="#f59e0b">
              <div className="stat-label">Pending</div>
              <div className="stat-value">{requests.length}</div>
              <div className="stat-sub">awaiting confirmation</div>
              <div className="stat-icon">⏳</div>
            </StatCard>
            <StatCard $color="#2563eb">
              <div className="stat-label">Total (GBP)</div>
              <div className="stat-value">£{totalGBP.toLocaleString(undefined, { maximumFractionDigits: 2 })}</div>
              <div className="stat-sub">pending value</div>
              <div className="stat-icon">💷</div>
            </StatCard>
            <StatCard $color="#059669">
              <div className="stat-label">By Currency</div>
              <div className="stat-value" style={{ fontSize: 14, marginTop: 4 }}>
                🇬🇧 {gbpCount} &nbsp;·&nbsp; 🇵🇰 {pkrCount} &nbsp;·&nbsp; 🇺🇸 {usdCount}
              </div>
              <div className="stat-sub">GBP · PKR · USD</div>
              <div className="stat-icon">🌍</div>
            </StatCard>
            <StatCard $color="#7c3aed">
              <div className="stat-label">Filtered</div>
              <div className="stat-value">{filtered.length}</div>
              <div className="stat-sub">matching view</div>
              <div className="stat-icon">🔍</div>
            </StatCard>
          </StatsRow>

          {/* ── Filter bar ── */}
          <FilterBar>
            <FilterLabel>Currency</FilterLabel>
            <FilterGroup>
              {["All", "GBP", "PKR", "USD"].map((cur) => {
                const cfg = CURRENCIES[cur];
                return (
                  <FilterChip
                    key={cur}
                    $active={filterCur === cur}
                    $color={cfg?.color || "#64748b"}
                    $bg={cfg?.bg || "#f1f5f9"}
                    onClick={() => setFilterCur(cur)}
                  >
                    {cfg?.flag} {cur}
                  </FilterChip>
                );
              })}
            </FilterGroup>
            <SearchInput
              placeholder="Search folder, agent, bank, reference…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </FilterBar>

          {/* ── Request cards ── */}
          <Grid>
            {filtered.length === 0 && (
              <EmptyState>
                <div className="icon">📭</div>
                <div className="msg">No pending payment requests</div>
                <div className="sub">
                  {search || filterCur !== "All"
                    ? "Try adjusting your filters"
                    : "All payments have been processed"}
                </div>
              </EmptyState>
            )}

            {filtered.map((req, i) => {
              const cur    = CURRENCIES[req.currency] || CURRENCIES.GBP;
              const rawAmt = req.currency === "PKR" ? req.paymentPKR
                           : req.currency === "USD" ? req.paymentUSD
                           : req.paymentGBP;
              return (
                <PaymentCard key={req.id} $color={cur.color} $i={i} onClick={() => openModal(req)}>
                  <CardTop>
                    <CurrencyTag $color={cur.color} $bg={cur.bg}>{cur.flag} {req.currency}</CurrencyTag>
                    <StatusPill $s="PENDING">PENDING</StatusPill>
                  </CardTop>

                  <CardField><span className="lbl">Folder</span><span className="mono">#{req.folderNo}</span></CardField>
                  <CardField><span className="lbl">Agent</span><span className="val">{req.agent}</span></CardField>
                  <CardField><span className="lbl">Bank</span><span className="val">{req.bankName}</span></CardField>
                  <CardField><span className="lbl">Date</span><span className="val">{req.date}</span></CardField>
                  {req.reference && req.reference !== "-" && (
                    <CardField><span className="lbl">Ref</span><span className="mono">{req.reference}</span></CardField>
                  )}

                  <GBPBadge>
                    <span className="label">
                      {cur.symbol}{(parseFloat(rawAmt) || 0).toLocaleString()}
                      {req.currency !== "GBP" && ` ${req.currency}`}
                    </span>
                    <span className="amount">£{(parseFloat(req.paymentGBP) || 0).toFixed(2)}</span>
                  </GBPBadge>
                </PaymentCard>
              );
            })}
          </Grid>

          {/* ── Review Modal ── */}
          {selected && editData && selCur && (
            <Overlay onClick={closeModal}>
              <Modal onClick={(e) => e.stopPropagation()}>

                <ModalHeader>
                  <ModalTitleWrap $bg={selCur.bg}>
                    <div className="icon-wrap">{selCur.flag}</div>
                    <div>
                      <h3>Review Payment</h3>
                      <span>Folder #{selected.folderNo} · {selected.agent}</span>
                    </div>
                  </ModalTitleWrap>
                  <CloseBtn onClick={closeModal}>×</CloseBtn>
                </ModalHeader>

                <ModalBody>

                  {/* GBP live highlight */}
                  <GBPHighlight>
                    <div>
                      <div className="hl-label">GBP Equivalent</div>
                      {editData.currency !== "GBP" && (
                        <div className="hl-rate">
                          {selCur.symbol}
                          {(parseFloat(nativeAmt()) || 0).toLocaleString()} {editData.currency}
                          {editData.rate ? ` ÷ ${editData.rate}` : ""}
                        </div>
                      )}
                    </div>
                    <div className="hl-amount">£{liveGBP().toFixed(2)}</div>
                  </GBPHighlight>

                  <EditHint>All fields are editable — changes will be saved to the ledger on confirm</EditHint>

                  <SectionDivider>Payment Info</SectionDivider>
                  <InfoGrid>

                    <InfoRow>
                      <div className="info-label">Agent</div>
                      <EditInput
                        value={editData.agent}
                        onChange={(e) => setField("agent", e.target.value)}
                        placeholder="Agent name"
                      />
                    </InfoRow>

                    <InfoRow>
                      <div className="info-label">Folder No</div>
                      <EditInput
                        $mono
                        value={editData.folderNo}
                        onChange={(e) => setField("folderNo", e.target.value)}
                        placeholder="Folder number"
                      />
                    </InfoRow>

                    <InfoRow>
                      <div className="info-label">Bank</div>
                      <EditInput
                        value={editData.bankName}
                        onChange={(e) => setField("bankName", e.target.value)}
                        placeholder="Bank name"
                      />
                    </InfoRow>

                    <InfoRow>
                      <div className="info-label">Payment Date</div>
                      <EditInput
                        type="date"
                        value={editData.date}
                        onChange={(e) => setField("date", e.target.value)}
                      />
                    </InfoRow>

                    <InfoRow>
                      <div className="info-label">Currency</div>
                      <EditSelect
                        value={editData.currency}
                        onChange={(e) => setField("currency", e.target.value)}
                      >
                        {CURRENCY_OPTIONS.map((c) => (
                          <option key={c} value={c}>{CURRENCIES[c].flag} {c}</option>
                        ))}
                      </EditSelect>
                    </InfoRow>

                    <InfoRow>
                      <div className="info-label">Exchange Rate</div>
                      <EditInput
                        $mono
                        type="number"
                        value={editData.rate}
                        onChange={(e) => setField("rate", e.target.value)}
                        placeholder={editData.currency === "GBP" ? "N/A" : "e.g. 355"}
                        disabled={editData.currency === "GBP"}
                      />
                    </InfoRow>

                    {/* Amount fields — show the relevant native amount prominently */}
                    {editData.currency === "GBP" && (
                      <InfoRow $span>
                        <div className="info-label">Amount (GBP £)</div>
                        <EditInput
                          $mono
                          type="number"
                          value={editData.paymentGBP}
                          onChange={(e) => setField("paymentGBP", e.target.value)}
                          placeholder="0.00"
                        />
                      </InfoRow>
                    )}

                    {editData.currency === "PKR" && (
                      <>
                        <InfoRow>
                          <div className="info-label">Amount (PKR ₨)</div>
                          <EditInput
                            $mono
                            type="number"
                            value={editData.paymentPKR}
                            onChange={(e) => setField("paymentPKR", e.target.value)}
                            placeholder="0"
                          />
                        </InfoRow>
                        <InfoRow>
                          <div className="info-label">Amount (GBP £) override</div>
                          <EditInput
                            $mono
                            type="number"
                            value={editData.paymentGBP}
                            onChange={(e) => setField("paymentGBP", e.target.value)}
                            placeholder="auto-calculated"
                          />
                        </InfoRow>
                      </>
                    )}

                    {editData.currency === "USD" && (
                      <>
                        <InfoRow>
                          <div className="info-label">Amount (USD $)</div>
                          <EditInput
                            $mono
                            type="number"
                            value={editData.paymentUSD}
                            onChange={(e) => setField("paymentUSD", e.target.value)}
                            placeholder="0.00"
                          />
                        </InfoRow>
                        <InfoRow>
                          <div className="info-label">Amount (GBP £) override</div>
                          <EditInput
                            $mono
                            type="number"
                            value={editData.paymentGBP}
                            onChange={(e) => setField("paymentGBP", e.target.value)}
                            placeholder="auto-calculated"
                          />
                        </InfoRow>
                      </>
                    )}

                    <InfoRow $span>
                      <div className="info-label">Reference / Receipt</div>
                      <EditInput
                        $mono
                        value={editData.reference}
                        onChange={(e) => setField("reference", e.target.value)}
                        placeholder="Reference or receipt number"
                      />
                    </InfoRow>

                  </InfoGrid>

                  <SectionDivider>Agent Remarks</SectionDivider>
                  <AdminNotesArea
                    placeholder="Agent remarks…"
                    value={editData.remarks}
                    onChange={(e) => setField("remarks", e.target.value)}
                    style={{ background: "#fefce8", borderColor: "#fde68a", color: "#713f12" }}
                  />

                  <SectionDivider>Admin Notes</SectionDivider>
                  <AdminNotesArea
                    placeholder="Add confirmation notes or reason for rejection…"
                    value={adminNotes}
                    onChange={(e) => setAdminNotes(e.target.value)}
                  />

                </ModalBody>

                <ModalFooter>
                  <ConfirmBtn onClick={handleConfirm} disabled={saving}>
                    {saving ? "Saving…" : "✓ Confirm & Save to Ledger"}
                  </ConfirmBtn>
                  <RejectBtn onClick={handleReject} disabled={saving}>✗ Reject</RejectBtn>
                  <CancelBtn onClick={closeModal}>Cancel</CancelBtn>
                </ModalFooter>

              </Modal>
            </Overlay>
          )}

        </Inner>
      </Page>
    </>
  );
}